// src/pages/Admin/Verifications.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  CheckCircle,
  Cancel,
  VerifiedUser,
  Gavel,
  Description,
  Download,
  PictureAsPdf,
  Image,
  Person,
  Email,
  Phone,
  ExpandMore,
  Warning,
  HourglassEmpty,
  History,
  ArrowForward,
  FileCopy,
  LocationOn,
  Business,
  CreditCard,
  Fingerprint,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminVerifications = () => {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationTypeFilter, setVerificationTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(null);
  const [requestInfoDialog, setRequestInfoDialog] = useState(null);
  const [viewDocsDialog, setViewDocsDialog] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedVerification, setExpandedVerification] = useState(null);

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllVerifications();
      setVerifications(response.data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = ['all', 'pending', 'verified', 'rejected', 'info_required'];
    setStatusFilter(statusMap[newValue]);
  };

  const handleVerifyBuyer = async (id, action) => {
    try {
      await adminAPI.verifyBuyer(id, { 
        action, 
        verifiedBy: 'admin',
        verificationDate: new Date().toISOString()
      });
      toast.success(`Buyer ${action === 'approve' ? 'verified' : 'rejected'}`);
      fetchVerifications();
      setVerifyDialog(null);
    } catch (error) {
      console.error('Error verifying buyer:', error);
      toast.error('Failed to verify buyer');
    }
  };

  const handleRequestMoreInfo = async (id, message) => {
    try {
      await adminAPI.requestMoreInfo(id, { 
        message,
        requestedBy: 'admin'
      });
      toast.success('Information requested successfully');
      fetchVerifications();
      setRequestInfoDialog(null);
    } catch (error) {
      console.error('Error requesting info:', error);
      toast.error('Failed to request information');
    }
  };

  const stats = {
    total: verifications.length,
    pending: verifications.filter(v => v.status === 'pending').length,
    verified: verifications.filter(v => v.status === 'verified').length,
    rejected: verifications.filter(v => v.status === 'rejected').length,
    infoRequired: verifications.filter(v => v.status === 'info_required').length,
  };

  const filteredVerifications = verifications.filter(verification => {
    const matchesSearch = 
      verification.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      verification.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      verification.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || verification.status === statusFilter;
    const matchesType = verificationTypeFilter === 'all' || verification.type === verificationTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading verifications...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Buyer Verifications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and verify buyer identities and documents
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchVerifications}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Verifications
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#2196f315', color: '#2196f3' }}>
                  <VerifiedUser />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Pending Review
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800' }}>
                  <HourglassEmpty />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Verified
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.verified}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Info Required
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.infoRequired}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#00bcd415', color: '#00bcd4' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" icon={<VerifiedUser />} iconPosition="start" />
          <Tab 
            label="Pending" 
            icon={
              <Badge badgeContent={stats.pending} color="warning">
                <HourglassEmpty />
              </Badge>
            } 
            iconPosition="start" 
          />
          <Tab label="Verified" icon={<CheckCircle />} iconPosition="start" />
          <Tab label="Rejected" icon={<Cancel />} iconPosition="start" />
          <Tab label="Info Required" icon={<Warning />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification Type</InputLabel>
              <Select
                label="Verification Type"
                value={verificationTypeFilter}
                onChange={(e) => setVerificationTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Submitted Date</InputLabel>
              <Select label="Submitted Date" defaultValue="all">
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              startIcon={<FilterList />}
              variant="outlined"
              onClick={() => {}}
            >
              More Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Verifications Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Buyer</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Type</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Documents</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Submitted</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVerifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <VerifiedUser sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No verifications found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVerifications.map((verification) => (
                  <React.Fragment key={verification._id}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {verification.userName?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {verification.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {verification.userEmail}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {verification.companyName || 'Individual'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={verification.type}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {verification.documents?.map((doc, index) => (
                            <Chip
                              key={index}
                              label={doc.type}
                              size="small"
                              icon={<Description />}
                              onClick={() => setViewDocsDialog({ ...verification, selectedDoc: doc })}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={verification.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(verification.status)}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(verification.submittedAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(verification.submittedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {verification.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={() => setVerifyDialog({ ...verification, action: 'approve' })}
                                  color="success"
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  onClick={() => setVerifyDialog({ ...verification, action: 'reject' })}
                                  color="error"
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Request Info">
                                <IconButton
                                  size="small"
                                  onClick={() => setRequestInfoDialog(verification)}
                                  color="info"
                                >
                                  <Warning />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setExpandedVerification(expandedVerification === verification._id ? null : verification._id)}
                            >
                              <ExpandMore 
                                sx={{ 
                                  transform: expandedVerification === verification._id ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    {expandedVerification === verification._id && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', p: 3 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Buyer Information
                                </Typography>
                                <List dense>
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Person />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Full Name"
                                      secondary={verification.userName}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Email />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Email"
                                      secondary={verification.userEmail}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Phone />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Phone"
                                      secondary={verification.userPhone}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <LocationOn />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Address"
                                      secondary={verification.userAddress}
                                    />
                                  </ListItem>
                                </List>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Company Information
                                </Typography>
                                <List dense>
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Business />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Company Name"
                                      secondary={verification.companyName || 'N/A'}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Fingerprint />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Tax ID"
                                      secondary={verification.taxId || 'N/A'}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <CreditCard />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Registration Number"
                                      secondary={verification.registrationNumber || 'N/A'}
                                    />
                                  </ListItem>
                                  <Divider component="li" />
                                  <ListItem>
                                    <ListItemAvatar>
                                      <Avatar>
                                        <LocationOn />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary="Business Address"
                                      secondary={verification.businessAddress || 'N/A'}
                                    />
                                  </ListItem>
                                </List>
                              </Paper>
                            </Grid>

                            <Grid item xs={12}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Uploaded Documents
                                </Typography>
                                <Grid container spacing={2}>
                                  {verification.documents?.map((doc, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Description color="primary" />
                                            <Typography variant="subtitle2">
                                              {doc.type}
                                            </Typography>
                                          </Box>
                                          {doc.imageUrl && (
                                            <img
                                              src={doc.imageUrl}
                                              alt={doc.type}
                                              style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                                            />
                                          )}
                                          <Button
                                            size="small"
                                            startIcon={<Visibility />}
                                            onClick={() => setViewDocsDialog({ ...verification, selectedDoc: doc })}
                                            sx={{ mt: 1 }}
                                            fullWidth
                                          >
                                            View Document
                                          </Button>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Verify Buyer Dialog */}
      <Dialog
        open={Boolean(verifyDialog)}
        onClose={() => setVerifyDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {verifyDialog?.action === 'approve' ? 'Approve Verification' : 'Reject Verification'}
        </DialogTitle>
        <DialogContent>
          {verifyDialog && (
            <>
              <DialogContentText gutterBottom>
                {verifyDialog.action === 'approve' 
                  ? `Approve verification for ${verifyDialog.userName}?`
                  : `Reject verification for ${verifyDialog.userName}?`
                }
              </DialogContentText>
              <Box sx={{ my: 2 }}>
                <Alert severity={verifyDialog.action === 'approve' ? 'success' : 'warning'}>
                  {verifyDialog.action === 'approve' 
                    ? 'This will grant full access to the platform.'
                    : 'Buyer will need to resubmit documents.'
                  }
                </Alert>
              </Box>
              {verifyDialog.action === 'reject' && (
                <TextField
                  fullWidth
                  label="Rejection Reason"
                  multiline
                  rows={3}
                  placeholder="Please specify the reason for rejection..."
                  sx={{ mt: 2 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleVerifyBuyer(verifyDialog?._id, verifyDialog?.action)}
            color={verifyDialog?.action === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {verifyDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request More Info Dialog */}
      <Dialog
        open={Boolean(requestInfoDialog)}
        onClose={() => setRequestInfoDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request More Information</DialogTitle>
        <DialogContent>
          {requestInfoDialog && (
            <>
              <DialogContentText gutterBottom>
                Request additional information from {requestInfoDialog.userName}
              </DialogContentText>
              <TextField
                fullWidth
                label="Request Message"
                multiline
                rows={4}
                placeholder="Please specify what additional information or documents are required..."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestInfoDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleRequestMoreInfo(requestInfoDialog?._id, 'Please provide additional documents')}
            color="primary"
            variant="contained"
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog
        open={Boolean(viewDocsDialog)}
        onClose={() => setViewDocsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Document: {viewDocsDialog?.selectedDoc?.type}
        </DialogTitle>
        <DialogContent>
          {viewDocsDialog?.selectedDoc?.imageUrl && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img
                src={viewDocsDialog.selectedDoc.imageUrl}
                alt={viewDocsDialog.selectedDoc.type}
                style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDocsDialog(null)}>Close</Button>
          {viewDocsDialog?.selectedDoc?.imageUrl && (
            <Button
              startIcon={<Download />}
              onClick={() => window.open(viewDocsDialog.selectedDoc.imageUrl, '_blank')}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminVerifications;